import * as c from '@iamo/custody-lib';
import { IConnection } from './Connection';
import { IBunkerConsent } from './Transaction';

export enum ConsentResponse {
    Given = 'given',
    Rejected = 'rejected',
    Unknown = 'unknown',
}

export interface IChallengeClaim {
    'component-id': string;    // string <uuid> identifier of the component the claim is made against
    claim: any;  // claim made by the user
}

export interface IBunkerConsentPayload {
    key: string;        // public key
    secret: string;     // ???
    message?: string;    // message to sign
    transaction?: any;    // transaction to sign
    'wallet-address'?: string;   // multisig wallet address
}

export enum SecurityRating {
    Insufficient = 'insufficient',
    Secure = 'secure',
    Highlysecure = 'highlysecure',
}


export enum PolicyAction {
    None = 'none',
    Account = 'account',
    SignMessage = 'sign-message',
    SignTransaction = 'sign-transaction',
    AddComponent = 'component-add',
    AddPolicy = 'policy-add',
    Key = 'key-create',
}

export interface IPolicyComponent {
    title: string; // module title
    description: string; // module description or instruction
    'component-id'?: string; // string<uuid>, component identifier
    'module-name'?: string; // name of module this component is associated with
    fact?: any; // value of fact that should be stored
    'seed-id'?: string; // string <uuid> seed identifier if fact has been set in a multi - step process
    modified?: string; // < ISO8601 DateTime >
}

/**
 * The policy-class of the wallet
 */
export class Policy {

    // The blockchain property of the backend connection object.
    connection: IConnection

    /**
     * initializes the class
     * @param connection The backend connection object.
     */
    constructor(connection: IConnection){
        this.connection = connection
    }

    /**
     * Create new policy
     * T.B.D where to get consent response needs to be determined by bunker team.
     * @param consentResponse The consent response (T.B.D fixed value "given" is used atm, but will be changed)
     * @returns The policy-id
     */
    async createPolicy(
        consentResponse: ConsentResponse
    ): Promise<string> {
        return c.Policy.createPolicy(this.connection, consentResponse)
    }

    /**
     * Removes a policy
     * @param consentResponse The consent response (T.B.D fixed value "given" is used atm, but will be changed)
     * @param policyId The policy id to delete
     * @returns true if successful
     */
    async removePolicy(
        consentResponse: ConsentResponse,
        policyId: string
    ): Promise<boolean> {
        return c.Policy.removePolicy(this.connection, consentResponse, policyId)
    }

    /**
     * Updates a policy's components
     * @param consentResponse The consent response (T.B.D fixed value "given" is used atm, but will be changed)
     * @param policyId The policy id to update
     * @param components The new components
     * @returns true if successful
     */
    async updatePolicy(
        consentResponse: ConsentResponse,
        policyId: string,
        components: IPolicyComponent[]
    ): Promise<boolean> {
        return c.Policy.updatePolicy(this.connection, consentResponse, policyId, components)
    }

    /**
     * Verifies a policy (t.b.d what's this?)
     * @param action The action
     * @param components The components to verify
     * @returns The bunker response
     */
    async verifyPolicy(
        action: PolicyAction,
        components: IPolicyComponent[]
    ): Promise<{
        action: PolicyAction; // value: "none", action a policy is used for
        score: SecurityRating; // security rating for policy or module
        components: SecurityRating[]; // security rating for poilcy or module
    }> {
        return c.Policy.verifyPolicy(this.connection, action, components)
    }

    /**
     * Get all user's policies
     * @returns An array with the user's policies
     */
    async getUserPolicies(): Promise<string[]> {
        return c.Policy.getUserPolicies(this.connection)
    }

    /**
     * Adds a component to a policy
     * @param policyId The target policy id
     * @param component The component to add
     * @returns true if successful
     */
    async addComponentToPolicy(
        policyId: string,
        component: IPolicyComponent
    ): Promise<boolean> {
        return c.Policy.addComponentToPolicy(this.connection, policyId, component)
    }

    /**
     * Updates a component for a policy
     * @param policyId The target policy id
     * @param componentId The component id to update
     * @param component The new component
     * @returns true if successful
     */
    async updateComponentForPolicy(
        policyId: string,
        componentId: string,
        component: IPolicyComponent
    ): Promise<boolean> {
        return c.Policy.updateComponentForPolicy(this.connection, policyId, componentId, component)
    }

    /**
     * Gets policy components of policy id
     * @param policyId The target policy id
     * @returns The policy-components
     */
    async getPolicyComponents(
        policyId: string
    ): Promise<IPolicyComponent[]> {
        return c.Policy.getPolicyComponents(this.connection, policyId)
    }

    /**
     * Assign a policy to an action
     * @param action The action to be assigned a policy
     * @param entity The entity
     * @param policyId The target policy id
     * @returns true if successful
     */
    async assignPolicy(
        action: string,
        entity: string,
        policyId: string
    ): Promise<boolean> {
        return c.Policy.assignPolicy(this.connection, action, entity, policyId)
    }

    /**
     * Sets a default policy
     * @param policyId The target policy id
     * @returns true if successful
     */
    async setDefaultPolicy(
        policyId: string
    ): Promise<boolean> {
        return c.Policy.setDefaultPolicy(this.connection, policyId)
    }

    /**
     * The Challenge policy
     * @param policyId The target policy id
     * @param payload The payload
     * @param action The action to challenge
     * @param claims The claim
     * @returns 
     */
    async challengePolicy(
        policyId: string,
        payload: IBunkerConsentPayload,
        action: PolicyAction,
        claims: IChallengeClaim[]
    ): Promise<IBunkerConsent> {
        return c.Policy.challengePolicy(this.connection, policyId, payload, action, claims)
    }

}

export default Policy